'use client';

import { c as _c } from "react/compiler-runtime";
import { css, cx } from '@repo/pandacss/css';
import { useEffect, useId, useRef, useState } from 'react';
import { debounce } from '../_popover/debounce';
import { getPositionProps, getTailClassName, POPOVER_TAIL_SIZE_PX, type PopoverPosition } from '../_popover/utils';

/** Click-to-open disclosure popover. Uses `role="dialog"`, not passive ARIA tooltips. */
export function Tooltip(t0) {
  const $ = _c(64);
  const {
    trigger,
    position: t1,
    children,
    containerClassName,
    contentClassName,
    ariaLabel,
    ariaLabelledBy,
    shouldCloseOnWidthResizing: t2
  } = t0;
  const position = t1 === undefined ? "bottom-left" : t1;
  const shouldCloseOnWidthResizing = t2 === undefined ? false : t2;
  const id = useId();
  let t3;
  if ($[0] !== id) {
    let t4;
    if ($[2] === Symbol.for("react.memo_cache_sentinel")) {
      t4 = /[^a-zA-Z0-9]/g;
      $[2] = t4;
    } else {
      t4 = $[2];
    }
    t3 = id.replace(t4, "");
    $[0] = id;
    $[1] = t3;
  } else {
    t3 = $[1];
  }
  const sanitizedId = t3;
  const popoverId = `tooltip-${sanitizedId}`;
  const anchorName = `--tooltip-${sanitizedId}`;
  const popoverRef = useRef(null);
  const [supportsPopoverTarget, setSupportsPopoverTarget] = useState(true);
  const [isOpen, setIsOpen] = useState(false);
  let t4;
  let t5;
  if ($[3] === Symbol.for("react.memo_cache_sentinel")) {
    t4 = () => {
      setSupportsPopoverTarget("showPopover" in HTMLElement.prototype && "popoverTargetElement" in HTMLButtonElement.prototype);
    };
    t5 = [];
    $[3] = t4;
    $[4] = t5;
  } else {
    t4 = $[3];
    t5 = $[4];
  }
  useEffect(t4, t5);
  let t6;
  let t7;
  if ($[5] === Symbol.for("react.memo_cache_sentinel")) {
    t6 = () => {
      const el = popoverRef.current;
      if (!el) {
        return;
      }
      const onToggle = e => {
        setIsOpen((e as ToggleEvent).newState === "open");
      };
      el.addEventListener("toggle", onToggle);
      return () => el.removeEventListener("toggle", onToggle);
    };
    t7 = [];
    $[5] = t6;
    $[6] = t7;
  } else {
    t6 = $[5];
    t7 = $[6];
  }
  useEffect(t6, t7);
  let t8;
  let t9;
  if ($[7] !== shouldCloseOnWidthResizing) {
    t8 = () => {
      if (!shouldCloseOnWidthResizing) {
        return;
      }
      let lastWidth = window.innerWidth;
      const handleResize = debounce(() => {
        const currentWidth = window.innerWidth;
        if (currentWidth !== lastWidth) {
          lastWidth = currentWidth;
          popoverRef.current?.hidePopover();
        }
      }, 100);
      window.addEventListener("resize", handleResize);
      return () => window.removeEventListener("resize", handleResize);
    };
    t9 = [shouldCloseOnWidthResizing];
    $[7] = shouldCloseOnWidthResizing;
    $[8] = t8;
    $[9] = t9;
  } else {
    t8 = $[8];
    t9 = $[9];
  }
  useEffect(t8, t9);
  let t10;
  let t11;
  let t12;
  let t13;
  let t14;
  let t15;
  let t16;
  let t17;
  let t18;
  if ($[10] !== anchorName || $[11] !== ariaLabel || $[12] !== ariaLabelledBy || $[13] !== containerClassName || $[14] !== contentClassName || $[15] !== isOpen || $[16] !== popoverId || $[17] !== position || $[18] !== supportsPopoverTarget || $[19] !== trigger) {
    const tailClassName = getTailClassName(position);
    const {
      isTopPosition,
      horizontalAlignment
    } = getPositionProps(position);
    const translateX = horizontalAlignment === "left" ? "0" : horizontalAlignment === "center" ? "-50%" : "-100%";
    const translateY = isTopPosition ? `calc(-100% - ${POPOVER_TAIL_SIZE_PX}px)` : "0";
    const anchorTop = isTopPosition ? "anchor(top)" : `calc(anchor(bottom) + ${POPOVER_TAIL_SIZE_PX}px)`;
    const anchorLeft = horizontalAlignment === "left" ? "anchor(left)" : horizontalAlignment === "center" ? "anchor(50%)" : "anchor(right)";
    if ($[29] !== containerClassName) {
      t17 = cx(css({
        position: "relative",
        display: "inline-block"
      }), containerClassName);
      $[29] = containerClassName;
      $[30] = t17;
    } else {
      t17 = $[30];
    }
    let t19;
    if ($[31] !== anchorName) {
      t19 = {
        anchorName
      };
      $[31] = anchorName;
      $[32] = t19;
    } else {
      t19 = $[32];
    }
    let t20;
    if ($[33] !== popoverId || $[34] !== supportsPopoverTarget) {
      t20 = supportsPopoverTarget ? {
        popoverTarget: popoverId
      } : {
        onClick: () => {
          const popover = popoverRef.current;
          if (!popover) {
            return;
          }
          if (popover.matches(":popover-open")) {
            popover.hidePopover();
          } else {
            popover.showPopover();
          }
        }
      };
      $[33] = popoverId;
      $[34] = supportsPopoverTarget;
      $[35] = t20;
    } else {
      t20 = $[35];
    }
    let t21;
    if ($[36] === Symbol.for("react.memo_cache_sentinel")) {
      t21 = css({
        cursor: "pointer",
        bg: "[transparent]",
        border: "none",
        p: "0",
        display: "inline",
        textAlign: "left"
      });
      $[36] = t21;
    } else {
      t21 = $[36];
    }
    if ($[37] !== isOpen || $[38] !== popoverId || $[39] !== t19 || $[40] !== t20 || $[41] !== trigger) {
      t18 = <button type="button" style={t19} aria-expanded={isOpen} aria-controls={popoverId} aria-haspopup="dialog" {...t20} className={t21}>{trigger}</button>;
      $[37] = isOpen;
      $[38] = popoverId;
      $[39] = t19;
      $[40] = t20;
      $[41] = trigger;
      $[42] = t18;
    } else {
      t18 = $[42];
    }
    t10 = popoverRef;
    t11 = "auto";
    t12 = popoverId;
    t13 = "dialog";
    if ($[43] !== ariaLabel || $[44] !== ariaLabelledBy) {
      t14 = ariaLabelledBy ? {
        "aria-labelledby": ariaLabelledBy
      } : {
        "aria-label": ariaLabel
      };
      $[43] = ariaLabel;
      $[44] = ariaLabelledBy;
      $[45] = t14;
    } else {
      t14 = $[45];
    }
    const t22 = `${translateX} ${translateY}`;
    if ($[46] !== anchorLeft || $[47] !== anchorName || $[48] !== anchorTop || $[49] !== t22) {
      t15 = {
        positionAnchor: anchorName,
        top: anchorTop,
        left: anchorLeft,
        translate: t22,
        maxWidth: "85vw"
      };
      $[46] = anchorLeft;
      $[47] = anchorName;
      $[48] = anchorTop;
      $[49] = t22;
      $[50] = t15;
    } else {
      t15 = $[50];
    }
    t16 = cx(css({
      margin: "0",
      maxWidth: "[min(90vw,480px)]",
      bg: "common.white",
      color: "neutral.darker-2",
      border: "1px solid",
      borderColor: "neutral.lighter",
      rounded: "sm",
      boxShadow: "[0 1px 4px rgba(100,102,106,0.36),0 4px 9px rgba(100,102,106,0.10)]",
      p: "3",
      overflow: "visible",
      textAlign: "left"
    }), tailClassName, contentClassName);
    $[10] = anchorName;
    $[11] = ariaLabel;
    $[12] = ariaLabelledBy;
    $[13] = containerClassName;
    $[14] = contentClassName;
    $[15] = isOpen;
    $[16] = popoverId;
    $[17] = position;
    $[18] = supportsPopoverTarget;
    $[19] = trigger;
    $[20] = t10;
    $[21] = t11;
    $[22] = t12;
    $[23] = t13;
    $[24] = t14;
    $[25] = t15;
    $[26] = t16;
    $[27] = t17;
    $[28] = t18;
  } else {
    t10 = $[20];
    t11 = $[21];
    t12 = $[22];
    t13 = $[23];
    t14 = $[24];
    t15 = $[25];
    t16 = $[26];
    t17 = $[27];
    t18 = $[28];
  }
  let t19;
  if ($[51] !== children || $[52] !== t10 || $[53] !== t11 || $[54] !== t12 || $[55] !== t13 || $[56] !== t14 || $[57] !== t15 || $[58] !== t16) {
    t19 = <div ref={t10} popover={t11} id={t12} role={t13} {...t14} style={t15} className={t16}>{children}</div>;
    $[51] = children;
    $[52] = t10;
    $[53] = t11;
    $[54] = t12;
    $[55] = t13;
    $[56] = t14;
    $[57] = t15;
    $[58] = t16;
    $[59] = t19;
  } else {
    t19 = $[59];
  }
  let t20;
  if ($[60] !== t17 || $[61] !== t18 || $[62] !== t19) {
    t20 = <span className={t17}>{t18}{t19}</span>;
    $[60] = t17;
    $[61] = t18;
    $[62] = t19;
    $[63] = t20;
  } else {
    t20 = $[63];
  }
  return t20;
}